import {
  HubConnectionBuilder,
  HubConnection,
  HubConnectionState,
  HttpTransportType,
  IHttpConnectionOptions,
} from '@microsoft/signalr';
import pWaitFor from 'p-wait-for';

import * as Api from './Api';
import config from '@config';
import type {
  ICountResponse,
  INewMessagesCountResponse,
  IRealtimeMessageResponse,
} from './types';
import { TokenService } from '@services';
import { isTokenExpired } from './utils';

export enum SocketEventsEnum {
  ReceiveMessage = 'ReciveMessage',
  ConversationsCount = 'ConversationsCount',
  NewMessagesCount = 'NewMessagesCount',
}

type ListenerType<T> = (event: T) => void;

const defaultOptions: IHttpConnectionOptions = {
  transport: HttpTransportType.WebSockets,
  accessTokenFactory: async () => {
    const tokens = await TokenService.get();

    if (!tokens) {
      return '';
    }

    if (isTokenExpired(tokens.accessToken)) {
      await Api.refreshTokenRenewalController.renewToken();
      const newTokens = await TokenService.get();

      return newTokens?.accessToken ?? '';
    }

    return tokens.accessToken;
  },
};

class BaseSocketApi {
  private _url: string;
  private _options: IHttpConnectionOptions;
  private _connection: HubConnection;

  constructor(hubName: string, options?: IHttpConnectionOptions) {
    this._url = `${config.API_URL}/hubs/${hubName}`;
    this._options = Object.assign({}, defaultOptions, options);

    this._connection = new HubConnectionBuilder()
      .withUrl(this._url, this._options)
      .withAutomaticReconnect()
      .build();
  }

  get state() {
    return this._connection.state;
  }

  get isConnected() {
    return this._connection.state === HubConnectionState.Connected;
  }

  waitForDefiningState() {
    return pWaitFor(
      () =>
        this.state !== HubConnectionState.Connecting &&
        this.state !== HubConnectionState.Reconnecting &&
        this.state !== HubConnectionState.Disconnecting,
    );
  }

  async connect() {
    await this.waitForDefiningState();

    if (this.state === HubConnectionState.Disconnected) {
      return this._connection.start();
    }
  }

  async disconnect() {
    await this.waitForDefiningState();

    if (this.state === HubConnectionState.Connected) {
      return this._connection.stop();
    }
  }

  on<T>(eventName: SocketEventsEnum, listener: ListenerType<T>) {
    this._connection.on(eventName, listener);

    return () => this.off(eventName);
  }

  off(eventName: SocketEventsEnum) {
    this._connection.off(eventName);
  }

  removeAllEvents() {
    Object.values(SocketEventsEnum).forEach((eventName) => {
      this.off(eventName);
    });
  }
}

class ChatSocketApi extends BaseSocketApi {
  constructor() {
    super('chat');
  }

  onReceiveMessage(listener: ListenerType<IRealtimeMessageResponse>) {
    return this.on(SocketEventsEnum.ReceiveMessage, listener);
  }

  onConversationsCount(listener: ListenerType<ICountResponse>) {
    return this.on(SocketEventsEnum.ConversationsCount, listener);
  }

  onNewMessagesCount(
    listener: ListenerType<INewMessagesCountResponse>,
  ) {
    return this.on(SocketEventsEnum.NewMessagesCount, listener);
  }
}

class SocketApi {
  Chat = new ChatSocketApi();

  async connect() {
    return this.Chat.connect();
  }

  async disconnect() {
    return this.Chat.disconnect();
  }

  async reconnect() {
    await this.disconnect();
    return this.connect();
  }
}

export default new SocketApi();
